import React, { useState, useMemo } from "react";
import useApi, { doFetch } from "storybook-dashboard/utils/fetching";
import DataTable from "react-data-table-component";
import Im from "immutable";

import { confirmDelete } from "traec-react/utils/sweetalert";
import CustomDropdown from "./dropdown";
import { Modal, useModal } from "storybook-dashboard/components/modal";
import { formEventToData } from "../utils";

// Column definitions
const getTenantColumns = () => [
  {
    name: "ID",
    selector: (row) => row.get("uid").substring(0, 8),
    sortable: true,
    width: "100px",
  },
  {
    name: "Domain URL",
    selector: (row) => row.get("domain_url") || "",
    sortable: true,
    grow: 2,
  },
  {
    name: "Schema Name",
    selector: (row) => row.get("schema_name") || "",
    sortable: true,
    grow: 2,
  },
  {
    name: "Admin Company",
    selector: (row) => row.getIn(["admin_company", "name"]) || "",
    sortable: true,
    grow: 2,
  },
  {
    name: "Actions",
    cell: (row) => <ActionsCell tenant={row} />,
    width: "100px",
    right: true,
  },
];

// Actions cell component
function ActionsCell({ tenant }) {
  const dropDownItems = [
    {
      name: "Delete",
      onClick: () => deleteTenant(tenant),
    },
  ];

  return <CustomDropdown items={dropDownItems} />;
}

const deleteTenant = (tenant) => {
  let tenantId = tenant.get("uid");
  confirmDelete({
    text: `Are you sure you want to delete tenant ${tenant.get("domain_url")}?`,
    onConfirm: () => {
      doFetch(`/api/tenant/admin/tenant/${tenantId}/`, "DELETE").then(() => {
        location.reload();
      });
    },
  });
};

const createSubTenancy = (e) => {
  let data = formEventToData(e);

  doFetch("/api/tenant/admin/tenant/", "POST", data).then(() => {
    location.reload();
  });
};

function CreateSubTenantFormModal() {
  return (
    <Modal title="Create a new Sub-Tenancy">
      <form onSubmit={createSubTenancy}>
        <div className="form-group">
          <label>Domain URL</label>
          <input type="text" name="domain_url" className="form-control" placeholder="<auto generate>" />
        </div>
        <div className="form-group">
          <label>Admin Company</label>
          <input
            type="text"
            className="form-control"
            name="admin_company"
            placeholder="<default: this.admin_company>"
          />
        </div>
        <div className="form-group">
          <label>From Company ID (optional)</label>
          <input type="text" name="from_company" className="form-control" />
        </div>
        <button type="submit" className="btn btn-primary">
          Create Sub-Tenancy
        </button>
      </form>
    </Modal>
  );
}

export default function SubTenantAdmin() {
  const { data: tenants } = useApi("/api/tenant/admin/tenant/");
  const { setModal } = useModal();
  const [searchTerm, setSearchTerm] = useState("");

  const columns = useMemo(() => getTenantColumns(), []);

  // Convert to list and sort by domain_url
  const sortedTenants = useMemo(() => {
    return (tenants || Im.List()).sortBy((t) => {
      const domain = t.get("domain_url");
      return domain ? domain.toLowerCase() : "";
    });
  }, [tenants]);

  // Filter tenants based on search term
  const filteredTenants = useMemo(() => {
    if (!searchTerm.trim()) {
      return sortedTenants;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return sortedTenants.filter((tenant) => {
      const id = tenant.get("uid") || "";
      const domain = tenant.get("domain_url") || "";
      const schema = tenant.get("schema_name") || "";
      const company = tenant.getIn(["admin_company", "name"]) || "";

      return (
        id.toLowerCase().includes(lowerSearchTerm) ||
        domain.toLowerCase().includes(lowerSearchTerm) ||
        schema.toLowerCase().includes(lowerSearchTerm) ||
        company.toLowerCase().includes(lowerSearchTerm)
      );
    });
  }, [sortedTenants, searchTerm]);

  return (
    <>
      <h3>Sub-Tenancies</h3>
      <p>Sub-tenancies are tenants that are isolated with regards to the MetricTree, Reporting Packages and Reports.</p>

      <p>
        "Safe" operations in tenancies that will not affect other tenancies (including this parent tenancy) include:
      </p>
      <ul>
        <li>Editing of BaseMetrics</li>
        <li>Reporting input values</li>
        <li>Deleting Reports and Reporting Packages</li>
      </ul>

      <p>
        <b>Notes:</b>
      </p>
      <ul>
        <li>
          Company and Project objects that are created are NOT isolated within tenancies. These are in a commonly
          accessibly space. This is because a tenant may wish to have "live" business units, projects and data alongside
          "sandboxed/demo" projectsand data.
        </li>
        <li>
          By default the new Tenancy that is created will be empty (no BaseMetrics, Reporting Packages or Reports).
        </li>
        <li>
          Specify a "From Tenancy" when creating a new Tenancy to clone data from another Tenancy into the new Tenancy.
          This allows for creation of a "template" tenancy with a default/initial set of data.
        </li>
        <li>
          Specify a "From Company" when creating a new Tenancy to clone existing data from a Corporate tree in the "From
          Tenancy" into the new sub-tenancy.
        </li>
      </ul>

      <button className="btn btn-primary btn-sm mt-3 mb-3" onClick={() => setModal(<CreateSubTenantFormModal />)}>
        Create a new Sub-Tenancy
      </button>

      <div style={{ marginBottom: "16px" }}>
        <input
          type="text"
          className="form-control"
          placeholder="Search by ID, Domain URL, Schema Name, or Company..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: "400px" }}
        />
      </div>

      <DataTable
        columns={columns}
        data={filteredTenants.toArray()}
        pagination
        paginationPerPage={20}
        paginationRowsPerPageOptions={[10, 20, 50, 100]}
        highlightOnHover={true}
        striped={true}
        defaultSortFieldId={2} // Sort by Domain URL column by default
      />
    </>
  );
}
